import type { Card, CardDepositAddress, CardDepositAsset } from "../types/cards-api.types";

/** The card fields that decide whether the deposit panel applies and whether it may fetch. */
export type CardDepositSubject = Pick<Card, "id" | "status" | "card_status" | "integration_provider">;

/**
 * Only cards issued through the crypto integration have an on-chain deposit address. Compares the enum value
 * — the provider is never shown.
 */
export const hasCryptoDeposits = (card: CardDepositSubject): boolean =>
  card.integration_provider === "CRYPTO_MATE";

/**
 * The backend answers 409 for a card that is not ACTIVE, so the panel never asks then. Same status derivation
 * as `augmentCardWithMocks` (either field, case-insensitive); a missing status is NOT treated as active.
 */
export const isCardActiveForDeposits = (card: CardDepositSubject): boolean =>
  (card.status || card.card_status || "").toUpperCase() === "ACTIVE";

/** Network code → display name. Networks are chains, not providers, so they may be named on screen. */
const NETWORK_DISPLAY_NAMES: Record<string, string> = {
  POLYGON: "Polygon",
  BNB: "BNB Smart Chain",
  BSC: "BNB Smart Chain",
};

/** `POLYGON` → "Polygon"; an unknown code is title-cased (`BASE_MAINNET` → "Base Mainnet"). */
export function formatDepositNetwork(code: string): string {
  const known = NETWORK_DISPLAY_NAMES[code.toUpperCase()];
  if (known) return known;
  return code
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

/**
 * The accepted symbols as one localized "either" list — `USDT o USDC`, `USDT or USDC`, `USDT ou USDC` — for
 * the warning that names the only assets the address credits.
 */
export function formatAssetList(symbols: readonly string[], locale: string): string {
  try {
    return new Intl.ListFormat(locale, { style: "long", type: "disjunction" }).format(symbols);
  } catch {
    // An unsupported locale tag throws a RangeError; a plain list still says the right thing.
    return symbols.join(", ");
  }
}

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const readAsset = (value: unknown): CardDepositAsset | null => {
  if (!isObject(value) || typeof value.symbol !== "string" || !value.symbol) return null;
  return {
    symbol: value.symbol,
    name: typeof value.name === "string" ? value.name : value.symbol,
    contract_address: typeof value.contract_address === "string" ? value.contract_address : "",
  };
};

/**
 * Defensive read of the deposit-addresses body. Anything that is not an array — a paginated envelope from a
 * catch-all mock, an error page — is "no address", and an entry without an address string is dropped: a
 * deposit panel must never render a half-formed destination for real money.
 */
export function readDepositAddresses(data: unknown): CardDepositAddress[] {
  if (!Array.isArray(data)) return [];
  return data.flatMap((entry): CardDepositAddress[] => {
    if (!isObject(entry) || typeof entry.address !== "string" || !entry.address.trim()) return [];
    const assets = Array.isArray(entry.assets)
      ? entry.assets.map(readAsset).filter((asset): asset is CardDepositAsset => asset !== null)
      : [];
    return [
      {
        network: typeof entry.network === "string" ? entry.network : "",
        address: entry.address.trim(),
        assets,
      },
    ];
  });
}

/**
 * Splits an address into the ends a person checks against their wallet (`0x` + 4, last 4 — what wallets show
 * when they shorten one) and the middle. Display only: the address is always rendered and copied whole.
 */
export function splitAddressForVerification(address: string): { head: string; middle: string; tail: string } {
  const headLength = address.startsWith("0x") ? 6 : 4;
  const tailLength = 4;
  if (address.length <= headLength + tailLength) return { head: address, middle: "", tail: "" };
  return {
    head: address.slice(0, headLength),
    middle: address.slice(headLength, address.length - tailLength),
    tail: address.slice(address.length - tailLength),
  };
}
